import { annotateClickAnchor } from './ara.js';
import type { AdSlot } from './types.js';

/**
 * Render an ad slot into a publisher container.
 *
 * Banner HTML snippets are placed inside a sandboxed `<iframe srcdoc>` so the
 * creative cannot read parent cookies, document.cookie, or localStorage.
 *
 * Sizing depends on `opts.responsive`:
 *  - responsive=true → iframe + anchor stretch to 100% of the slot, height
 *    pinned to the creative's design height (AdSense "full-width-responsive"
 *    behaviour). The creative HTML uses width:100% flex inside so the layout
 *    grows gracefully into the extra horizontal space.
 *  - responsive=false → iframe + anchor at the creative's native pixel size,
 *    centred in the slot. A publisher who set `data-size="300x250"` gets
 *    exactly 300×250, no surprises.
 *
 * The click URL on the wrapping <a> is the *signed* beacon URL — Tracking will
 * verify it and 302 to the actual landing page.
 */
export function renderAd(
  container: HTMLElement,
  ad: AdSlot,
  opts: { responsive?: boolean } = {},
): void {
  container.innerHTML = '';
  const responsive = opts.responsive ?? false;

  const anchor = document.createElement('a');
  anchor.href = ad.beacons.click;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer sponsored';
  // Phase 8.2 — Attribution Reporting API source registration on the click
  // anchor. No-op outside Chromium; with ARA support the click both
  // navigates AND registers the conversion source against the beacon URL.
  annotateClickAnchor(anchor);
  anchor.style.cssText = responsive
    ? 'display:block;text-decoration:none;width:100%;'
    : `display:block;text-decoration:none;width:${ad.width}px;max-width:100%;margin:0 auto;`;

  if (ad.html) {
    const iframe = document.createElement('iframe');
    iframe.title = `ad-${ad.creative_id}`;
    const h = ad.height > 0 ? ad.height : 250;
    // pointer-events:none lets the click bubble up to the <a>. The creative
    // is decorative so there's nothing inside to interact with.
    iframe.style.cssText = responsive
      ? `border:0;display:block;width:100%;height:${h}px;pointer-events:none;`
      : `border:0;display:block;width:${ad.width}px;height:${h}px;pointer-events:none;`;
    iframe.setAttribute('sandbox', 'allow-popups allow-popups-to-escape-sandbox');
    iframe.referrerPolicy = 'no-referrer-when-downgrade';
    iframe.srcdoc = wrapSnippet(ad.html);
    anchor.appendChild(iframe);
  } else if (ad.image_url) {
    const img = document.createElement('img');
    img.src = ad.image_url;
    img.alt = '';
    img.referrerPolicy = 'no-referrer-when-downgrade';
    img.style.cssText = responsive
      ? `display:block;border:0;width:100%;height:auto;`
      : `display:block;border:0;width:${ad.width}px;height:${ad.height}px;`;
    anchor.appendChild(img);
  } else {
    // Defensive fallback: no creative provided.
    return;
  }

  container.appendChild(anchor);
}

/**
 * Wrap raw HTML snippet in a minimal HTML envelope inside the iframe so the
 * snippet runs as a document (and inline `<script>` tags execute) but with no
 * top-level access.
 */
function wrapSnippet(html: string): string {
  return (
    '<!doctype html><html><head>' +
    '<meta charset="utf-8">' +
    '<meta http-equiv="X-Content-Type-Options" content="nosniff">' +
    '<style>html,body{margin:0;padding:0;width:100%;height:100%;overflow:hidden;}' +
    'a,a *{cursor:pointer;}</style></head>' +
    '<body>' +
    html +
    '</body></html>'
  );
}
